/**
 * 出貨管理列表(REQ-20260806002)。表格樣式沿用 .claude/skills/ui-guidelines §1。
 *
 * 每列的動作依狀態給(REQ-20260806005,編輯放寬到 POSTED 是 REQ-20260807001):
 *   DRAFT       過帳 / 編輯 / 刪除
 *   POSTED      編輯 / 作廢(兩者都是「已沖帳就不給」,後端擋)
 *   HISTORICAL  作廢 —— 舊資料匯入單不給編輯
 *   VOIDED      不給任何動作
 * 列印不分狀態都在 —— 列印頁本來就是唯讀呈現。
 *
 * 作廢另有第二個入口:編輯模式下頂端工具列的「本單作廢」(REQ-20260812002),規則相同。
 *
 * REQ-20260815008 另加「出貨」欄與動作:出貨狀態是後端聚合出貨紀錄算的,
 * 登記出貨只給 POSTED(草稿還沒定案、作廢 / 歷史單不該再出)。
 *
 * REQ-20260817002 再加「出貨狀況」篩選 —— 純前端過濾當頁資料,見下方 filteredItems。
 *
 * REQ-20260820004 起,「狀態」下拉的第一個選項「全部狀態」= 有效單據(草稿 + 已過帳),
 * 不再包含已作廢 / 歷史單 —— 要看那兩種得從同一個下拉手動選,見下方 status 的預設值。
 *
 * REQ-20260820006 起,篩選條件(狀態 / 出貨狀況 / 關鍵字 / 排序)受控於網址參數,
 * 不再是離開畫面就沒了的本地 state,見下方 FILTER_DEFAULTS。
 *
 * REQ-20260821001 起,**已沖帳的單在畫面上直接鎖死**:編輯 / 刪除 / 作廢的入口
 * 不再出現,改顯示「已沖帳,鎖定」。這推翻了 REQ-20260807001 §三 的
 * 「讓使用者按得下去、由後端回 400」——使用者明確要求前端鎖死(見該 REQ 文件)。
 * **後端的守門一行都沒有拿掉**,前端只是不給入口。
 * 要改已沖帳的單,動線是:應收沖帳頁「⚠️ 解除沖銷」→ 回到未收款 → 這裡才解鎖。
 * REQ-20260824009 只調整鎖定時**深連結進來看到什麼**:從「表單整個不渲染」改成
 * 「說明 + 唯讀表單」(不能修改 ≠ 不能查閱),鎖定規則與後端守門都沒變。
 *
 * REQ-20260824005 起,「出貨」與「編輯」還多看 `shipmentStatus`:**已出完貨
 * (`FULL`)的 POSTED 單兩顆都不出現**。編輯是把後端早就有的守門(REQ-20260815008
 * §1.4「已有出貨紀錄不可修改」)搬到入口,不必填完整份表單才被 400 擋;出貨則是
 * 出完的單再開登記彈窗只會看到一份不能勾的表單,容易誤會還沒出完。
 * 作廢 / 作廢並複製新單 / 折讓單 / 列印**刻意不看出貨狀態** —— 那是出完貨之後
 * 要退單改單的正式管道。
 *
 * REQ-20260825004 把上面那兩顆拆開:**「出貨」對 `FULL` 改成顯示「出貨紀錄」並照常
 * 開彈窗**(REQ-20260825003 之後,彈窗裡的歷次出貨紀錄可以修正出貨日期,而這是
 * 唯一入口 —— 收掉按鈕等於讓那個功能沒有地方按)。**「編輯」的規則一個字都沒改**:
 * 整份表單的數量 / 品項對已出完貨的單依然不給改,那是後端守門的範圍。
 *
 * REQ-20260929002 加上分頁(每頁 PAGE_SIZE 筆)與期間篩選(月份,或勾「自訂區間」
 * 改成起訖日),兩者都下推到後端、也都寫進網址。在此之前清單只拿後端預設的最新 50 筆,
 * 又沒有翻頁 UI,更早的單在畫面上根本找不到。
 * ⚠️ **「出貨狀況」篩選與欄位排序仍然只作用於目前這一頁**(REQ-20260817002 的刻意決定,
 * 見下方 filteredItems / rows):「共 N 筆」是後端依狀態 / 關鍵字 / 期間算的總數,
 * 不含出貨狀況;排序也只重排這一頁的 50 筆,不是全部單據。
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ORDER_SHIPMENT_STATUS_LABELS, type SalesOrderListItem } from '@app/shared';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import {
  useDeleteSalesOrder,
  useDuplicateSalesOrder,
  usePostSalesOrder,
  useSalesOrder,
  useSalesOrders,
  useVoidSalesOrder,
} from '../accounting/api';
import { CreditNoteModal } from './CreditNoteModal';
import { isOrderSettled } from './order-lock';
import { SalesOrderForm } from './SalesOrderForm';
import { ShipmentModal } from './ShipmentModal';

/**
 * 「已沖帳,鎖定」的判斷(REQ-20260821001)。
 *
 * 用列表已經帶回來的 `settledAmount`(後端 `toListItem()` 的既有欄位),
 * 不必為了鎖定另外開一支 API。規則本身住在 `order-lock.ts`(REQ-20260824009 抽出去)
 * —— 列印預覽頁的「修改訂單」與表單的唯讀模式用的是同一條,這裡只負責取欄位。
 */
function isSettled(order: SalesOrderListItem): boolean {
  return isOrderSettled(order.settledAmount);
}

/** 鎖定時顯示的標示(取代編輯 / 刪除 / 作廢那幾顆) */
function LockedTag({ orderId }: { orderId: number }): JSX.Element {
  return (
    <span
      className="text-slate-400"
      title="這張單已經收過款,不能再修改或作廢。要改請先到「應收沖帳」解除沖銷。"
      data-testid={`order-locked-${orderId}`}
    >
      已沖帳,鎖定
    </span>
  );
}

const SORT_KEYS = [
  'orderNo',
  'txnDate',
  'customerSimpleName',
  'salesPersonName',
  'total',
  'outstanding',
] as const;

type SortKey = (typeof SORT_KEYS)[number];

/**
 * 篩選條件與它們的預設值(REQ-20260820006)。key 就是網址參數名 ——
 * 這五個現在存在網址上而不是本地 state,等於預設值時不寫進網址。
 *
 * 為什麼要搬:使用者篩好清單 →「編輯」→「儲存修改」→ 列印頁 →「關閉頁面」
 * 回到 `/accounting?tab=sales` 時,本地 state 早就跟著卸載了,清單整個重置成預設值,
 * 得從頭再篩一次。放在網址上,回來的那個網址帶什麼就還原什麼。
 * 讀寫方式沿用 CustomerConsolePage / SupplierConsolePage 既有的 useSearchParams 模式。
 *
 * REQ-20260929002 再加五個:頁碼 `page`、月份 `period`、自訂區間開關 `range`('1' = 勾選)
 * 與起訖日 `dfrom` / `dto`。⚠️ 起訖日**不能叫 `from` / `to`**:這一頁的 `from` 早就是
 * 深連結「返回來源頁」用的參數(見 trustedFrom(),REQ-20260904001),撞名會把
 * 返回動線打壞。
 */
const FILTER_DEFAULTS = {
  status: 'ACTIVE',
  ship: '',
  q: '',
  sort: 'txnDate',
  dir: 'desc',
  page: '1',
  period: '',
  range: '',
  dfrom: '',
  dto: '',
} as const;

type FilterKey = keyof typeof FILTER_DEFAULTS;

/** 改了不必回第 1 頁的參數:排序只重排目前這一頁;page 本身就是翻頁 */
const PAGE_KEEPING_KEYS: readonly FilterKey[] = ['sort', 'dir', 'page'];

/** 每頁筆數(REQ-20260929002)。與後端 list() 不帶 limit 時的預設值相同 */
const PAGE_SIZE = 50;

const STATUS_LABELS: Record<string, string> = {
  DRAFT: '草稿',
  POSTED: '已過帳',
  VOIDED: '已作廢',
  HISTORICAL: '歷史單',
};

/**
 * 需要先確認才做的動作:刪草稿要再問一次,作廢還要收原因。
 *
 * `void-duplicate`(REQ-20260821001)是「作廢並複製新單」—— 與 `void` 走**完全同一條**
 * 確認 / 送出路徑,只是成功之後多一步:複製出一張新草稿並把使用者帶去編輯。
 * 做成同一個 kind 的變體而不是另一組確認 UI,是因為它就是「作廢」加一個後續動作。
 */
interface PendingAction {
  id: number;
  orderNo: string;
  kind: 'delete' | 'void' | 'void-duplicate';
  /**
   * 從頂端工具列的「本單作廢」按的(REQ-20260812002)。確認列改渲染在工具列下方,
   * 不進表格 —— 編輯中的那張單可能因為搜尋 / 狀態篩選根本不在下方清單裡
   * (深連結進來時尤其常見),掛在表格列上會變成「按了沒反應」。
   */
  fromToolbar?: boolean;
}

/**
 * 成功訊息。`orderId` 有值時多給一個「查看 / 列印」連結(REQ-20260811004)——
 * 建單完的下一步幾乎都是把藝品印刷訂單印出來,而這裡原本只有一句話,
 * 使用者得自己回清單找那張單。訪談「新增應收單」成功後早就是這個互動,兩邊對齊。
 *
 * 「儲存修改」已經不走這裡了(REQ-20260811005 改成直接導頁到列印頁)。
 */
interface Flash {
  message: string;
  orderId?: number;
}

export function SalesOrdersPanel({
  initialEditOrderId,
  initialShipOrderId,
  initialShipOrderNo,
}: {
  /** 深連結帶進來的單:掛載後自動開它的編輯表單(REQ-20260811001) */
  initialEditOrderId?: number | undefined;
  /** 深連結帶進來的單:掛載後自動開它的出貨登記彈窗(REQ-20260815009) */
  initialShipOrderId?: number | undefined;
  /** 出貨彈窗標題要顯示的單號 —— 與 `initialShipOrderId` 成對,兩個都有才套用 */
  initialShipOrderNo?: string | undefined;
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const readFilter = (key: FilterKey): string => searchParams.get(key) ?? FILTER_DEFAULTS[key];
  /**
   * 篩選條件寫回網址(REQ-20260820006)。一次可以改多個 key ——
   * 排序要同時換欄位與方向,分兩次寫會拿到過期的 searchParams。
   * 等於預設值就從網址拿掉,不要留一串跟沒篩一樣的參數;
   * 一律 `replace` —— 在搜尋框打字不該一個字一筆瀏覽紀錄。
   *
   * 換篩選條件就回第 1 頁(REQ-20260929002)—— 留在第 3 頁換條件,幾乎一定是空清單。
   * 集中在這裡做,新增篩選條件時不會漏掉;排序與翻頁本身不算(PAGE_KEEPING_KEYS)。
   */
  const setFilters = (patch: Partial<Record<FilterKey, string>>): void => {
    const next = new URLSearchParams(searchParams);
    const keys = Object.keys(patch) as FilterKey[];
    if (keys.some((key) => !PAGE_KEEPING_KEYS.includes(key))) next.delete('page');
    for (const [key, value] of Object.entries(patch) as [FilterKey, string][]) {
      if (value === FILTER_DEFAULTS[key]) next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  /**
   * 狀態篩選(REQ-20260820004)。預設 `'ACTIVE'` 而不是空字串 —— 空字串等於不帶
   * status,後端會把已作廢與歷史單一起撈出來,日常辦公畫面太雜。`'ACTIVE'` 由後端
   * 展開成 `DRAFT + POSTED`,選 `VOIDED` / `HISTORICAL` 仍是各自的精確比對。
   */
  const status = readFilter('status');
  /**
   * 出貨狀況篩選(REQ-20260817002)。刻意不進後端 query —— 出貨狀態是後端當場聚合
   * 出貨紀錄算出來的、不是 salesOrder 上的欄位,要下推得改查詢;而列表本來就已經
   * 把整頁的 shipmentStatus 帶回來了,就地過濾這一頁即可。
   */
  const shipmentStatusFilter = readFilter('ship');
  const keyword = readFilter('q');
  /** 網址上的排序欄位可能被人亂改 —— 不認得就退回預設,免得 sortRows 拿 undefined 去比 */
  const rawSortKey = readFilter('sort') as SortKey;
  const sortKey: SortKey = SORT_KEYS.includes(rawSortKey) ? rawSortKey : 'txnDate';
  const sortDir: 'asc' | 'desc' = readFilter('dir') === 'asc' ? 'asc' : 'desc';
  /** 網址上的頁碼同樣可能被亂改 —— 不是正整數就退回第 1 頁 */
  const rawPage = Number(readFilter('page'));
  const page = Number.isInteger(rawPage) && rawPage >= 1 ? rawPage : 1;
  /**
   * 期間(REQ-20260929002)。月份留空 = 不限期間(跟加這個篩選之前一樣從最新列起);
   * 勾「自訂區間」就改用起訖日,寫法照抄應收沖帳(REQ-20260815013)。
   * 起訖日留空 = 那一端不限。兩組只會送其中一組給後端。
   */
  const period = readFilter('period');
  const customRange = readFilter('range') === '1';
  const dateFrom = readFilter('dfrom');
  const dateTo = readFilter('dto');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [flash, setFlash] = useState<Flash | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** 正在登記出貨的那張單(REQ-20260815008)。彈窗自己去查進度,這裡只記是哪張 */
  const [shipmentFor, setShipmentFor] = useState<{ id: number; orderNo: string } | null>(null);
  /** 正在開折讓單的那張單(REQ-20260821001) */
  const [creditNoteFor, setCreditNoteFor] = useState<SalesOrderListItem | null>(null);
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useSalesOrders({
    status: status || undefined,
    keyword: keyword.trim() || undefined,
    ...(customRange
      ? { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }
      : { period: period || undefined }),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const postMutation = usePostSalesOrder();
  const deleteMutation = useDeleteSalesOrder();
  const voidMutation = useVoidSalesOrder();
  const duplicateMutation = useDuplicateSalesOrder();
  const editing = useSalesOrder(editingId ?? undefined);

  // 出貨狀況篩選與排序都只作用於**目前這一頁**(REQ-20260929002 加分頁後的已知限制)——
  // 第 1 頁篩不到「待出貨」不代表第 2 頁也沒有;要看某段期間的待出貨單,先選期間再篩。
  const filteredItems = useMemo(() => {
    const items = data?.items ?? [];
    if (!shipmentStatusFilter) return items;
    return items.filter((o) => o.shipmentStatus === shipmentStatusFilter);
  }, [data, shipmentStatusFilter]);
  const rows = useMemo(
    () => sortRows(filteredItems, sortKey, sortDir),
    [filteredItems, sortKey, sortDir],
  );

  /** 編輯中且明細已載入的那張單 —— 頂端「本單作廢」按鈕的依據(REQ-20260812002) */
  const editingOrder = editingId != null ? editing.data : undefined;

  /**
   * 深連結進來時直接開編輯表單(REQ-20260811001)。走的是跟「點編輯」完全同一條路徑,
   * 只套用一次 —— 使用者取消或改看別張之後不該再被拉回去。
   * 單號存不存在不預先驗證,沿用 useSalesOrder 既有的載入失敗顯示。
   */
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (initialEditOrderId == null || deepLinkApplied.current) return;
    deepLinkApplied.current = true;
    setEditingId(initialEditOrderId);
  }, [initialEditOrderId]);

  /**
   * 深連結進來時直接開出貨登記彈窗(REQ-20260815009)—— 訪談的出貨徽章與儀表板的
   * 未出貨清單走這條。同樣只套用一次:關掉彈窗之後不該再被拉回來。
   *
   * 刻意跟上面的編輯深連結分開:兩者互不相關(通常只會來其中一個),
   * 共用一個 ref 會讓先到的那個把另一個永久擋掉。
   * 單號是否真的存在 / 是不是 POSTED 不預先驗證,交給彈窗自己的載入與守門顯示。
   */
  const shipDeepLinkApplied = useRef(false);
  useEffect(() => {
    if (initialShipOrderId == null || !initialShipOrderNo || shipDeepLinkApplied.current) return;
    shipDeepLinkApplied.current = true;
    setShipmentFor({ id: initialShipOrderId, orderNo: initialShipOrderNo });
  }, [initialShipOrderId, initialShipOrderNo]);

  /**
   * 開表單時把畫面捲到表單區塊(REQ-20260821004)。
   *
   * 表單一律渲染在畫面最上方,而清單可以很長 —— 使用者捲到中段按「編輯」或
   * 「作廢並複製新單」,畫面停在原地不動,看起來就像按了沒反應。
   *
   * 放在這裡而不是各個 onClick 裡:編輯 / 複製新單 / 新增 / 深連結最後都是改這兩個
   * state,集中成一個 effect 就不會有哪個入口漏掉。`requestAnimationFrame` 是等表單
   * 那一輪畫完才捲(寫法沿用 ReceivablePanel 的「查看收款沖帳紀錄」)。
   */
  useEffect(() => {
    if (editingId == null && !showForm) return;
    requestAnimationFrame(() => {
      document
        .getElementById('sales-order-form-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [editingId, showForm]);

  /**
   * 深連結帶進來的來源頁,不可信就當作沒帶。判斷沿用「儲存修改」那一段
   * (REQ-20260825008),REQ-20260904001 把它抽出來給出貨彈窗的兩個收尾共用 ——
   * 規則一字未改,只是不要同一條白名單散在三個地方。
   *
   * 網址是使用者可以隨手改的輸入,「有帶就跳」等於把這些收尾變成任意跳轉點。
   * 比對的是 `/customers/console` 而不是 `/customers` —— 別「簡化」成後者。
   */
  const trustedFrom = (): string | null => {
    const raw = searchParams.get('from');
    return raw && (raw.startsWith('/accounting') || raw.startsWith('/customers/console'))
      ? raw
      : null;
  };

  /*
   * 使用者主動關掉出貨彈窗(X / Escape / 「關閉」鈕)時的收尾。深連結那兩個入口
   * (訪談的出貨徽章、儀表板的未出貨清單)都是 `target="_blank"`,登記完關掉彈窗後
   * 停在一個空的會計頁上,回原本那頁還得自己關分頁 —— 這裡先試著把分頁收掉。
   *
   * 判斷條件與列印頁的「關閉頁面」同一套(REQ-20260809001):`window.history.length <= 1`
   * 才是「剛開出來、只有一筆 history」的新分頁;**不用 `window.opener`** ——
   * 那兩個連結都帶 `rel="noreferrer"`,opener 一律是 null。
   *
   * 但這裡多要一個 `shipDeepLinkApplied` 條件:使用者也可能就在會計頁上按「出貨」,
   * 而那個分頁的 history 剛好也是一筆(例如重新整理過)。只看長度會把使用者
   * 唯一的工作分頁關掉。同分頁的情境維持原行為,只收彈窗、不導頁。
   *
   * `window.close()` 之後**一定**再導一次儀表板(REQ-20260825010):瀏覽器原則上只讓
   * 腳本關掉「腳本自己開的」視窗,使用者點連結開的新分頁多半會被擋,而且是**靜默**
   * 失敗 —— 不丟例外、也沒有回傳值可判斷,實測就是分頁沒關掉、人被留在背景那張
   * 會計出貨清單上(REQ-20260817001 押在關分頁會成功,這是它沒料到的)。
   * 所以「回到原本那頁」不能只靠關分頁:關得掉的話分頁即將消失,這行導頁無害;
   * 關不掉的話至少畫面會落在下面那段挑的地方,而不是一張使用者沒打算來的清單。
   *
   * 保底目的地看深連結有沒有帶可信的 `from`(REQ-20260904001)。**訪談的出貨徽章**
   * 會帶上「這位客戶的詳細頁」,關不掉分頁時就回到那位客戶 —— REQ-20260817001 的
   * 原始需求本來就是「返回原點進入的頁面」,`from=` 才第一次讓它做得到。
   * **儀表板的未出貨清單**不帶 `from`,照樣退回 `/dashboard`,那條入口一個字都沒變。
   *
   * (REQ-20260825010 當時把 `from=` 評為過度設計,但自己留了後路:「若日後使用者反映
   * 『從訪談進去、關掉後想回訪談』,再開需求處理」—— 本次正是那個情境。)
   */
  const closeShipmentModal = (): void => {
    setShipmentFor(null);
    if (shipDeepLinkApplied.current && window.history.length <= 1) {
      window.close();
      navigate(trustedFrom() ?? '/dashboard');
    }
  };

  /*
   * 送出成功(按下「確定出貨」)之後的收尾(REQ-20260826002)。使用者實際的動線是
   * 從儀表板的未出貨清單一筆一筆處理,做完要回首頁看整體帳務有沒有動 ——
   * 留在會計出貨管理清單上等於看不出剛才那一筆的影響。
   *
   * **條件刻意只看 `shipDeepLinkApplied`,不跟 `closeShipmentModal` 一樣再要一個
   * `window.history.length <= 1`。** 那個長度判斷是「要不要關分頁」才需要的守門 ——
   * 關錯會關掉使用者唯一的工作分頁,代價很高。導頁沒有這個風險(最壞就是多按一次
   * 上一頁),而儀表板那個入口是**同分頁的 `<Link>`**(`DashboardPage.tsx`,不是
   * `target="_blank"`),history 一定不只一筆。把長度條件抄過來,這條路徑會永遠
   * 不成立 —— 本次需求等於沒做。要改這裡之前先確認你不是在重蹈這一步。
   *
   * 非深連結(在會計頁自己按「出貨」)維持原行為:彈窗留著,可以連續登記下一筆。
   *
   * 落點改成看 `from`(REQ-20260904001,**部分**反轉上面那段的「固定導儀表板」):
   * 從**訪談的出貨徽章**進來的網址帶著那位客戶的詳細頁,送出成功就回那位客戶 ——
   * 他要確認的是那筆訪談的徽章有沒有變成「已出貨 + 日期」,儀表板上看不到這件事。
   * 從**儀表板未出貨清單**進來的沒帶 `from`,仍然退回 `/dashboard`,
   * REQ-20260826002 為那條主動線做的行為完全不變。
   *
   * 不呼叫 `window.close()`:這次的重點是讓使用者**看到**回去那一頁的變化,
   * 關掉分頁只會把人丟回背景那張還沒重查的舊頁面。
   */
  const handleShipped = (): void => {
    if (!shipDeepLinkApplied.current) return;
    setShipmentFor(null);
    navigate(trustedFrom() ?? '/dashboard');
  };

  /** 動作前先把上一輪的訊息清掉,免得成功訊息跟新的錯誤訊息同時掛在畫面上 */
  const startAction = (next: PendingAction | null): void => {
    setPending(next);
    setVoidReason('');
    setFlash(null);
    setActionError(null);
  };

  const confirmDelete = (target: PendingAction): void => {
    deleteMutation.mutate(target.id, {
      onSuccess: () => {
        setPending(null);
        if (editingId === target.id) setEditingId(null);
        setFlash({ message: `草稿 ${target.orderNo} 已刪除。` });
      },
      onError: (err) => setActionError(getApiErrorMessage(err, '刪除失敗')),
    });
  };

  const confirmVoid = (target: PendingAction): void => {
    const reason = voidReason.trim();
    if (!reason) {
      setActionError('請填寫作廢原因');
      return;
    }
    voidMutation.mutate(
      { id: target.id, reason },
      {
        onSuccess: () => {
          setPending(null);
          setVoidReason('');
          // 作廢掉的單不能再編輯,還開著的編輯表單要收掉(與 confirmDelete 同一條收尾)
          if (editingId === target.id) setEditingId(null);
          if (target.kind === 'void-duplicate') {
            duplicate(target.id, `${target.orderNo} 已作廢`);
            return;
          }
          setFlash({ message: `${target.orderNo} 已作廢。` });
        },
        // 已有沖帳紀錄時後端會擋,把原話透出來,不要吞掉
        onError: (err) => setActionError(getApiErrorMessage(err, '作廢失敗')),
      },
    );
  };

  /**
   * 複製成新草稿並直接開它的編輯表單(REQ-20260821001)。
   *
   * 作廢已經成功了,複製失敗不該讓使用者以為整件事沒發生 ——
   * 所以錯誤訊息會把「已作廢」那一半講清楚(`prefix`),新單再自己建一次即可。
   */
  const duplicate = (id: number, prefix: string): void => {
    duplicateMutation.mutate(id, {
      onSuccess: (created) => {
        setShowForm(false);
        setEditingId(created.id);
        setFlash({
          message: `${prefix},已複製成新草稿 ${created.orderNo},請確認內容後過帳。`,
          orderId: created.id,
        });
      },
      onError: (err) =>
        setActionError(`${prefix},但${getApiErrorMessage(err, '複製新單失敗')}`),
    });
  };

  const onSort = (key: SortKey): void => {
    // 點同一欄切方向,換一欄從 asc 開始(行為與改成網址參數之前相同)
    const dir = key === sortKey && sortDir === 'asc' ? 'desc' : 'asc';
    setFilters({ sort: key, dir });
  };

  /**
   * 「回到這份清單」的網址(REQ-20260820006)。導去列印頁時用 `?from=` 一起帶過去,
   * 列印頁的「關閉頁面」才回得到使用者離開前的篩選條件 —— 沒有它的話,
   * 那顆按鈕導回的是寫死的 `/accounting?tab=sales`,篩選條件一樣會全部重置。
   *
   * 刻意只帶篩選條件,**不帶 editOrderId / shipOrderId 那類深連結參數**:
   * 回來要看到的是清單,不是又被打開一次的編輯表單(REQ-20260811005 §四)。
   * 白名單就是 FILTER_DEFAULTS 的 key,所以頁碼與期間(REQ-20260929002)也會跟著帶回來。
   */
  const listUrl = (): string => {
    const params = new URLSearchParams({ tab: 'sales' });
    for (const key of Object.keys(FILTER_DEFAULTS) as FilterKey[]) {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    }
    return `/accounting?${params.toString()}`;
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={keyword}
          onChange={(e) => setFilters({ q: e.target.value })}
          placeholder="搜尋單號 / 客戶 / 電話"
          className="border rounded-md px-3 py-1.5 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setFilters({ status: e.target.value })}
          aria-label="狀態"
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          {/* 「全部狀態」= 有效單據(草稿 + 已過帳),不含已作廢 / 歷史單(REQ-20260820004) */}
          <option value="ACTIVE">全部狀態</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        {/* 出貨狀況(REQ-20260817002)。與左邊的「狀態」不同,這個是就地過濾已經抓回來的
            那一頁,不會重新發請求 —— 換頁 / 換關鍵字後仍以當頁資料為準。 */}
        <select
          value={shipmentStatusFilter}
          onChange={(e) => setFilters({ ship: e.target.value })}
          aria-label="出貨狀況"
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">全部出貨狀況</option>
          {Object.entries(ORDER_SHIPMENT_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        {/* 期間(REQ-20260929002)。版面與文字照抄應收沖帳的「對帳期間」(REQ-20260815013):
            預設是月份,勾「自訂區間」才換成起訖日。取消勾選時一併清掉起訖日,
            網址上不留一組沒在用的條件。 */}
        <div className="flex items-center gap-1">
          <label className="text-sm text-slate-600" htmlFor={customRange ? 'so-dfrom' : 'so-period'}>
            期間
          </label>
          {customRange ? (
            <>
              <input
                id="so-dfrom"
                type="date"
                value={dateFrom}
                onChange={(e) => setFilters({ dfrom: e.target.value })}
                aria-label="起日"
                className="border rounded-md px-2 py-1.5 text-sm"
              />
              <span className="text-slate-400">~</span>
              <input
                id="so-dto"
                type="date"
                value={dateTo}
                onChange={(e) => setFilters({ dto: e.target.value })}
                aria-label="迄日"
                className="border rounded-md px-2 py-1.5 text-sm"
              />
            </>
          ) : (
            <input
              id="so-period"
              type="month"
              value={period}
              onChange={(e) => setFilters({ period: e.target.value })}
              aria-label="月份"
              className="border rounded-md px-3 py-1.5 text-sm"
            />
          )}
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={customRange}
              onChange={(e) =>
                setFilters(e.target.checked ? { range: '1' } : { range: '', dfrom: '', dto: '' })
              }
              data-testid="so-custom-range"
            />
            自訂區間
          </label>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingId(null);
            setShowForm((v) => !v);
          }}
          className="ml-auto bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
        >
          {showForm ? '收起' : '+ 新增出貨單'}
        </button>
        {/* 編輯中的單直接在這裡作廢(REQ-20260812002)。規則與清單那顆「作廢」完全相同
            (POSTED / HISTORICAL 才給、已沖帳由後端擋),走的也是同一條
            startAction / confirmVoid —— 只是使用者不必退回清單才找得到它。 */}
        {editingOrder &&
        (editingOrder.status === 'POSTED' || editingOrder.status === 'HISTORICAL') &&
        !isSettled(editingOrder) ? (
          <button
            type="button"
            onClick={() =>
              startAction({
                id: editingOrder.id,
                orderNo: editingOrder.orderNo,
                kind: 'void',
                fromToolbar: true,
              })
            }
            className="border border-red-300 text-red-600 px-3 py-1.5 rounded hover:bg-red-50"
            data-testid="order-void-btn-top"
          >
            本單作廢
          </button>
        ) : null}
      </div>

      {/* 工具列那顆的確認列。刻意不掛在表格列上:編輯中的單不一定在目前的篩選結果裡。
          離開編輯模式(收掉表單、改按新增)就跟著收起 —— 它屬於這次編輯。 */}
      {pending?.fromToolbar && pending.id === editingOrder?.id ? (
        <div
          className="flex flex-wrap items-center justify-end gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm"
          data-testid="order-void-confirm-top"
        >
          <label className="mr-auto flex items-center gap-2 text-slate-700">
            作廢 {pending.orderNo} 的原因
            <input
              type="text"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="border rounded-md px-2 py-1 text-sm"
              data-testid="order-void-reason-top"
            />
          </label>
          <button
            type="button"
            disabled={voidMutation.isPending}
            onClick={() => confirmVoid(pending)}
            className="text-red-600 hover:underline disabled:opacity-50"
          >
            確定作廢
          </button>
          <button
            type="button"
            onClick={() => startAction(null)}
            className="text-slate-600 hover:underline"
          >
            取消
          </button>
        </div>
      ) : null}

      {flash ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 text-sm"
        >
          {flash.message}
          {/* 建單這條路徑維持開新分頁、不自動導頁 —— 剛建好的是草稿,使用者通常還要
              接著過帳或處理清單上的別張單(與「新增應收單」成功畫面的列印連結同一個互動)。
              「儲存修改」則是直接導頁過去,見上面編輯表單的 onCreated。 */}
          {flash.orderId != null ? (
            <Link
              to={`/sales-orders/${flash.orderId}/print`}
              target="_blank"
              rel="noreferrer"
              className="border border-blue-600 text-blue-700 rounded px-2 py-0.5 hover:bg-blue-50"
              data-testid="order-flash-print-link"
            >
              查看 / 列印藝品印刷訂單
            </Link>
          ) : null}
        </div>
      ) : null}

      {actionError ? (
        <div
          role="alert"
          className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 text-sm"
          data-testid="order-action-error"
        >
          {actionError}
        </div>
      ) : null}

      {/* 表單區塊的捲動目標(REQ-20260821004)。不論表單開不開都掛著,上面那個 effect
          才有固定的 id 可以捲過去;`empty:hidden` 是沒開表單時不要多佔一格 grid 間距。 */}
      <div id="sales-order-form-section" className="empty:hidden">
        {editingId != null ? (
          editing.isLoading ? (
            <div role="status" className="text-slate-500">
              載入單據明細中...
            </div>
          ) : editing.isError || !editing.data ? (
            <div role="alert" className="text-red-600">
              載入失敗：{getApiErrorMessage(editing.error, '未知錯誤')}
            </div>
          ) : isSettled(editing.data) ? (
            /*
             * 已沖帳的單:**說明 + 唯讀表單**(REQ-20260824009)。清單上那顆「編輯」
             * 已經不會出現,但深連結(`?editOrderId=`)還是進得來 —— 那條路徑
             * 是別的畫面帶進來的,不能假設它知道這張單的沖帳狀態。
             *
             * 這裡修正了 REQ-20260821001 §3.1 的做法:當時表單**整個不渲染**,
             * 結果「不能修改」變成連「不能查閱」—— 使用者點進來只看得到一段文字,
             * 看不到單上任何內容。現在照樣鎖死(表單唯讀、沒有儲存 / 刪除 / 新增列),
             * 只是看得到每一格。**後端的 400 守門一行都沒動**,前端仍然不給編輯入口。
             */
            <div className="grid gap-3">
              <div
                role="note"
                className="text-sm text-slate-700 bg-slate-50 border rounded px-3 py-2"
                data-testid="order-locked-notice"
              >
                <p>
                  出貨單 {editing.data.orderNo} 已收款 {formatMoney(editing.data.settledAmount)},
                  已鎖定為唯讀,不能修改或作廢。以下內容僅供查閱。
                </p>
                <p className="mt-1 text-slate-500">
                  要修改請先到「應收沖帳」找到對應的收款紀錄按「⚠️ 解除沖銷」;
                  若只是要退款給客戶,改用「折讓單」即可,原帳不動。
                </p>
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  className="mt-2 text-blue-600 hover:underline"
                >
                  回清單
                </button>
              </div>
              <SalesOrderForm
                key={editing.data.id}
                editOrder={editing.data}
                readOnly
                /* 唯讀模式沒有送出鈕,這支永遠不會被呼叫 —— 只是型別上要給 */
                onCreated={() => undefined}
                onCancel={() => setEditingId(null)}
              />
            </div>
          ) : (
            <SalesOrderForm
              key={editing.data.id}
              editOrder={editing.data}
              onCreated={(order) => {
                /*
                 * 存完直接進列印頁(REQ-20260811005)。改單的下一步就是把改過的
                 * 藝品印刷訂單重印一份,使用者明確要求「按下儲存修改就跳過去」,
                 * 不要再回清單自己點連結(REQ-20260811004 當時是給連結不導頁)。
                 * 同分頁導覽 —— 不開新分頁,所以列印頁的「關閉頁面」按鈕也跟著要
                 * 認得這條路徑(見 OrderPrintPage)。
                 *
                 * `?from=` 帶的是「使用者離開前的這份清單」(REQ-20260820006),
                 * 讓列印頁按「關閉頁面」回得到同一組篩選條件。
                 *
                 * REQ-20260825008:網址上**已經帶了** `from` 就優先用它 —— 那代表
                 * 使用者是從別的地方深連結進來的(例如客戶訪談紀錄的單號徽章),
                 * 存完檔要回的是那裡,不是會計的出貨管理清單。沒帶才退回 `listUrl()`,
                 * 也就是從清單本身按「編輯」進來的原行為,完全不變。
                 * (REQ-20260904001 把這段白名單判斷抽成 `trustedFrom()`,與出貨彈窗的
                 * 收尾共用 —— 規則沒有變,行為也沒有變。)
                 */
                const backTo = trustedFrom() ?? listUrl();
                navigate(`/sales-orders/${order.id}/print?from=${encodeURIComponent(backTo)}`);
              }}
              onCancel={() => setEditingId(null)}
              /* 表單裡的刪除入口(REQ-20260812001)。收尾與列表那顆刪除鈕一樣:
                 收掉表單、回清單、綠色 flash —— 只是這條路徑的單一定是編輯中的那張。 */
              onDeleted={() => {
                setEditingId(null);
                setFlash({ message: `草稿 ${editing.data.orderNo} 已刪除。` });
              }}
            />
          )
        ) : showForm ? (
          <SalesOrderForm
            onCreated={(order) => {
              setShowForm(false);
              setFlash({
                message: `已建立出貨單 ${order.orderNo}(草稿)。確認無誤後按「過帳」即進入應收帳款。`,
                orderId: order.id,
              });
            }}
            onCancel={() => setShowForm(false)}
          />
        ) : null}
      </div>

      {isLoading ? (
        <div role="status" className="text-slate-500">
          載入中...
        </div>
      ) : isError ? (
        <div role="alert" className="text-red-600">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm">
          目前沒有出貨單。按右上角「+ 新增出貨單」即可建立。
        </div>
      ) : (
        <table className="w-full bg-white shadow-sm rounded-md">
          <thead>
            <tr className="border-b text-left text-sm text-slate-600">
              <SortableTh label="單號" k="orderNo" active={sortKey} dir={sortDir} onSort={onSort} />
              <SortableTh label="日期" k="txnDate" active={sortKey} dir={sortDir} onSort={onSort} />
              <SortableTh label="客戶" k="customerSimpleName" active={sortKey} dir={sortDir} onSort={onSort} />
              <SortableTh label="業務員" k="salesPersonName" active={sortKey} dir={sortDir} onSort={onSort} />
              <th className="px-4 py-2">狀態</th>
              <th className="px-4 py-2">出貨</th>
              <SortableTh label="含稅總計" k="total" active={sortKey} dir={sortDir} onSort={onSort} />
              <SortableTh label="未結" k="outstanding" active={sortKey} dir={sortDir} onSort={onSort} />
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <Fragment key={o.id}>
                <tr className="border-b last:border-0 hover:bg-slate-50 text-sm">
                  <td className="px-4 py-2 whitespace-nowrap font-mono">{o.orderNo}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{o.txnDate}</td>
                  <td className="px-4 py-2 max-w-xs truncate">{o.customerSimpleName}</td>
                  {/* 歷史單沒有業務員(欄位在此之前沒有任何入口寫得進去)—— 顯示「未指定」 */}
                  <td className="px-4 py-2 whitespace-nowrap text-slate-600">
                    {o.salesPersonName ?? '未指定'}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{STATUS_LABELS[o.status] ?? o.status}</td>
                  {/* 出貨進度(REQ-20260815008)。未出完的用琥珀色標出來 ——
                      這一欄存在的理由就是「一眼看出哪些單還積著貨沒出」。 */}
                  <td
                    className={`px-4 py-2 whitespace-nowrap ${
                      o.shipmentStatus === 'FULL' ? 'text-slate-600' : 'text-amber-700'
                    }`}
                    data-testid={`order-shipment-status-${o.id}`}
                  >
                    {o.shipmentStatus ? ORDER_SHIPMENT_STATUS_LABELS[o.shipmentStatus] : '—'}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right font-mono">
                    {formatMoney(o.total)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right font-mono">
                    {formatMoney(o.outstanding)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    <span className="inline-flex items-center gap-3">
                      {o.status === 'DRAFT' ? (
                        <button
                          type="button"
                          disabled={postMutation.isPending}
                          onClick={() =>
                            postMutation.mutate(o.id, {
                              onSuccess: () =>
                                setFlash({ message: `${o.orderNo} 已過帳,已計入應收帳款。` }),
                            })
                          }
                          className="text-blue-600 hover:underline disabled:opacity-50"
                        >
                          過帳
                        </button>
                      ) : null}
                      {/* 已過帳但還沒被沖帳的單也可以改(REQ-20260807001)——
                          訪談建的應收單一律直接 POSTED,不放寬就永遠只能作廢重開。
                          已沖帳的單按下去後端會回 400,訊息顯示在上方 alert 區塊。 */}
                      {/* 登記出貨(REQ-20260815008)。只給 POSTED —— 與後端的守門同一條線;
                          按下去開彈窗,可以只出部分品項、也可以分多次出完。
                          出完貨的單改叫「出貨紀錄」(REQ-20260825004):REQ-20260824005
                          當初把這顆收掉,是因為當時彈窗對出完的單真的沒事可做;
                          REQ-20260825003 讓歷次出貨紀錄可以改日期之後,那裡變成
                          「發現出貨日期 KEY 錯了要去修」的唯一入口,不能再擋。
                          文字換掉是為了不讓人誤會這張單還能再登記新的出貨。 */}
                      {o.status === 'POSTED' ? (
                        <button
                          type="button"
                          onClick={() => setShipmentFor({ id: o.id, orderNo: o.orderNo })}
                          className="text-blue-600 hover:underline"
                          data-testid={`order-ship-btn-${o.id}`}
                        >
                          {o.shipmentStatus === 'FULL' ? '出貨紀錄' : '出貨'}
                        </button>
                      ) : null}
                      {/* 已沖帳的單一律鎖死(REQ-20260821001):編輯 / 刪除 / 作廢都不給,
                          改顯示標示與 tooltip。折讓單與列印不受影響 —— 那兩個
                          正是「已收款的單還能做什麼」的答案。 */}
                      {/* 已出完貨的 POSTED 單也不給編輯(REQ-20260824005)——
                          後端 `update()` 本來就會回「已有出貨紀錄,不可修改」,
                          前端不擋的話要填完整份表單才被 400 擋下。草稿不必查:
                          登記出貨只放行 POSTED,草稿不可能有出貨紀錄。 */}
                      {(o.status === 'DRAFT' ||
                        (o.status === 'POSTED' && o.shipmentStatus !== 'FULL')) &&
                      !isSettled(o) ? (
                        <button
                          type="button"
                          onClick={() => {
                            startAction(null);
                            setShowForm(false);
                            setEditingId(o.id);
                          }}
                          className="text-blue-600 hover:underline"
                          data-testid={`order-edit-btn-${o.id}`}
                        >
                          編輯
                        </button>
                      ) : null}
                      {o.status === 'DRAFT' ? (
                        <button
                          type="button"
                          onClick={() =>
                            startAction({ id: o.id, orderNo: o.orderNo, kind: 'delete' })
                          }
                          className="text-red-600 hover:underline"
                          data-testid={`order-delete-btn-${o.id}`}
                        >
                          刪除
                        </button>
                      ) : null}
                      {(o.status === 'POSTED' || o.status === 'HISTORICAL') && !isSettled(o) ? (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              startAction({ id: o.id, orderNo: o.orderNo, kind: 'void' })
                            }
                            className="text-red-600 hover:underline"
                            data-testid={`order-void-btn-${o.id}`}
                          >
                            作廢
                          </button>
                          {/* 需求的「解除沖銷後,前面的訂單才能作廢並複製新單」——
                              解除沖銷後這張單回到未收款,這顆才會出現 */}
                          <button
                            type="button"
                            onClick={() =>
                              startAction({
                                id: o.id,
                                orderNo: o.orderNo,
                                kind: 'void-duplicate',
                              })
                            }
                            className="text-red-600 hover:underline"
                            data-testid={`order-void-duplicate-btn-${o.id}`}
                          >
                            作廢並複製新單
                          </button>
                        </>
                      ) : null}
                      {/* 折讓單:已進應收的單都給,**含已沖帳的**(跨月改帳的正解) */}
                      {o.status === 'POSTED' || o.status === 'HISTORICAL' ? (
                        <button
                          type="button"
                          onClick={() => {
                            startAction(null);
                            setCreditNoteFor(o);
                          }}
                          className="text-blue-600 hover:underline"
                          data-testid={`order-credit-note-btn-${o.id}`}
                        >
                          折讓單
                        </button>
                      ) : null}
                      {/* 已作廢的單只剩「複製新單」—— 作廢那一步已經做過了 */}
                      {o.status === 'VOIDED' ? (
                        <button
                          type="button"
                          disabled={duplicateMutation.isPending}
                          onClick={() => {
                            startAction(null);
                            duplicate(o.id, `${o.orderNo} 已作廢`);
                          }}
                          className="text-blue-600 hover:underline disabled:opacity-50"
                          data-testid={`order-duplicate-btn-${o.id}`}
                        >
                          複製新單
                        </button>
                      ) : null}
                      {isSettled(o) ? <LockedTag orderId={o.id} /> : null}
                      {/* REQ-20260806004:舊單事後補印的入口。任何狀態都給,列印頁本來就是唯讀呈現 */}
                      <Link
                        to={`/sales-orders/${o.id}/print`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                        data-testid={`order-print-link-${o.id}`}
                      >
                        列印
                      </Link>
                    </span>
                  </td>
                </tr>

                {/* 從工具列按的作廢,確認列在上面那塊,這裡不重複渲染一份 */}
                {pending?.id === o.id && !pending.fromToolbar ? (
                  <tr className="border-b last:border-0 bg-amber-50 text-sm">
                    <td colSpan={7} className="px-4 py-2">
                      {pending.kind === 'delete' ? (
                        <div
                          className="flex flex-wrap items-center justify-end gap-2"
                          data-testid={`order-delete-confirm-${o.id}`}
                        >
                          <span className="mr-auto text-slate-700">
                            確定要刪除草稿 {o.orderNo}?刪除後無法復原。
                          </span>
                          <button
                            type="button"
                            disabled={deleteMutation.isPending}
                            onClick={() => confirmDelete(pending)}
                            className="text-red-600 hover:underline disabled:opacity-50"
                          >
                            確定刪除
                          </button>
                          <button
                            type="button"
                            onClick={() => startAction(null)}
                            className="text-slate-600 hover:underline"
                          >
                            取消
                          </button>
                        </div>
                      ) : (
                        <div
                          className="flex flex-wrap items-center justify-end gap-2"
                          data-testid={`order-void-confirm-${o.id}`}
                        >
                          <label className="mr-auto flex items-center gap-2 text-slate-700">
                            {pending.kind === 'void-duplicate'
                              ? `作廢 ${o.orderNo} 並複製成新草稿的原因`
                              : `作廢 ${o.orderNo} 的原因`}
                            <input
                              type="text"
                              value={voidReason}
                              onChange={(e) => setVoidReason(e.target.value)}
                              className="border rounded-md px-2 py-1 text-sm"
                              data-testid={`order-void-reason-${o.id}`}
                            />
                          </label>
                          <button
                            type="button"
                            disabled={voidMutation.isPending || duplicateMutation.isPending}
                            onClick={() => confirmVoid(pending)}
                            className="text-red-600 hover:underline disabled:opacity-50"
                          >
                            {pending.kind === 'void-duplicate' ? '確定作廢並複製' : '確定作廢'}
                          </button>
                          <button
                            type="button"
                            onClick={() => startAction(null)}
                            className="text-slate-600 hover:underline"
                          >
                            取消
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* 分頁(REQ-20260929002)。區塊照抄 ProductListPage;這一頁篩完是空的也照樣顯示 ——
          「出貨狀況」只過濾目前這一頁,別頁可能還有符合的單,不能把翻頁入口一起藏掉。
          「共 N 筆」是後端的總數,不含出貨狀況篩選(見檔頭)。 */}
      {!isLoading && !isError ? (
        <div className="flex justify-between items-center text-sm">
          <span className="text-slate-500">共 {total} 筆</span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setFilters({ page: String(Math.max(1, page - 1)) })}
              className="border rounded px-3 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="px-3 py-1">{page} / {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setFilters({ page: String(Math.min(totalPages, page + 1)) })}
              className="border rounded px-3 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        </div>
      ) : null}

      {/* 折讓單彈窗(REQ-20260821001)。同樣是開著才掛,關掉就卸載 */}
      {creditNoteFor ? (
        <CreditNoteModal
          open
          onClose={() => setCreditNoteFor(null)}
          orderId={creditNoteFor.id}
          orderNo={creditNoteFor.orderNo}
          orderTotal={creditNoteFor.total}
          customerName={creditNoteFor.customerFullName || creditNoteFor.customerSimpleName}
          txnDate={creditNoteFor.txnDate}
          firstProductName={creditNoteFor.firstProductName}
          lineCount={creditNoteFor.lineCount}
          onCreated={(documentNo) => {
            setCreditNoteFor(null);
            setFlash({
              message: `折讓單 ${documentNo} 已開立,會在套用期別的月結對帳單中扣除。`,
            });
          }}
        />
      ) : null}

      {/* 出貨彈窗(REQ-20260815008)。開著時才掛,關掉就整個卸載 —— 下次開是乾淨的表單 */}
      {shipmentFor ? (
        <ShipmentModal
          open
          onClose={closeShipmentModal}
          onShipped={handleShipped}
          orderId={shipmentFor.id}
          orderNo={shipmentFor.orderNo}
        />
      ) : null}
    </div>
  );
}

function SortableTh({
  label,
  k,
  active,
  dir,
  onSort,
}: {
  label: string;
  k: SortKey;
  active: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}): JSX.Element {
  const isActive = active === k;
  return (
    <th className="px-4 py-2">
      <button
        type="button"
        onClick={() => onSort(k)}
        className="inline-flex items-center gap-1 hover:text-blue-600"
        aria-label={`依${label}排序`}
      >
        {label}
        <span className={isActive ? 'text-blue-600 text-xs' : 'text-slate-300 text-xs'}>
          {isActive && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

/** 清單一次全載入,前端排序即可(見 ui-guidelines §2) */
function sortRows(
  items: SalesOrderListItem[],
  key: SortKey,
  dir: 'asc' | 'desc',
): SalesOrderListItem[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    if (key === 'total' || key === 'outstanding') {
      return (Number(a[key]) - Number(b[key])) * sign;
    }
    // 業務員可能是 null(歷史單沒有指定)—— 當成空字串排,不要讓 localeCompare 爆掉
    if (key === 'salesPersonName') {
      return (a.salesPersonName ?? '').localeCompare(b.salesPersonName ?? '', 'zh-Hant') * sign;
    }
    return a[key].localeCompare(b[key], 'zh-Hant') * sign;
  });
}
